import React, { FC, useMemo, useState } from "react";
import ImgCrop from "antd-img-crop";
import { Col, Typography, Upload } from "antd";
import { Icons } from "../vpd-icons/icons";
import { RcFile } from "antd/lib/upload/interface";
import { ImageUploaderImageDisplay } from "./image-uploader-image-display";
import styles from "./image-uploader.css";

const { ["image-uploader-loading"]: imgUploaderLoading } = styles;

type Props = {
  fileTypes?: string[];
  onBeforeUpload?: (image: RcFile) => Promise<boolean | void>;
  onChange?: (image: RcFile) => Promise<void>;
  uploadText?: string;
  allowCrop?: boolean;
  value?: { value: RcFile | null; existingImage?: string | null };
  loading: boolean;
};

export const ImageUploader: FC<Props> = ({
  fileTypes = ["png", "jpg", "jpeg", "gif"],
  onBeforeUpload,
  onChange,
  uploadText = "Select image",
  allowCrop = false,
  value: { existingImage } = { existingImage: null },
  loading,
}) => {
  const accept = useMemo(() => {
    return fileTypes?.reduce(
      (acc: string, item: string, index: number) =>
        (acc += `.${item}${index !== fileTypes.length - 1 ? "," : ""}`),
      ""
    );
  }, [fileTypes]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const uploadButton = (
    <div>
      {isLoading ? Icons.Loading : Icons["Add New"]}
      <div style={{ marginTop: 8 }}>{uploadText}</div>
    </div>
  );

  return (
    <>
      <Col>
        {!loading && (
          <>
            {allowCrop ? (
              <ImgCrop
                aspect={3}
                grid
                quality={1}
                zoom
                modalTitle="Crop image"
                modalOk="Select for upload"
                modalCancel="Cancel"
              >
                <Upload
                  accept={accept}
                  beforeUpload={async (img) => {
                    onBeforeUpload && (await onBeforeUpload(img));
                  }}
                  listType="picture-card"
                  maxCount={1}
                  onChange={async (info) => {
                    try {
                      setIsLoading(true);
                      onChange && (await onChange(info?.fileList[0]?.originFileObj as RcFile));
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                >
                  {uploadButton}
                </Upload>
              </ImgCrop>
            ) : (
              <Upload
                accept={accept}
                beforeUpload={async (img) => {
                  onBeforeUpload && (await onBeforeUpload(img));
                }}
                listType="picture-card"
                maxCount={1}
                onChange={async (info) => {
                  try {
                    setIsLoading(true);
                    onChange && (await onChange(info?.fileList[0]?.originFileObj as RcFile));
                  } finally {
                    setIsLoading(false);
                  }
                }}
              >
                {uploadButton}
              </Upload>
            )}
          </>
        )}
        {loading && <div className={imgUploaderLoading}>{Icons.Loading}</div>}
      </Col>
      {!loading && existingImage && (
        <Col>
          <Typography.Title level={5} style={{ marginBottom: 8 }}>
            Existing image
          </Typography.Title>
          <ImageUploaderImageDisplay value={existingImage} width={200} />
        </Col>
      )}
    </>
  );
};
